import { Button } from '@sweep-io/sweep-design';
import { Box, Stack } from '@mui/material';
import { useSelector } from 'react-redux';
import { useTerritoriesActions } from '../../hooks/useTerritoriesActions';
import { useEffect, useState } from 'react';
import RestrictedTooltip from '../common/permissions/RestrictedTooltip';
import { usePermissions } from '../../permissions/usePermissions';
import CreateEditTerritoryDialog from './CreateEditTerritoryDialog';
import { selectTerritories } from '../../reducers/territoriesReducer';
import { selectDefaultCreationCrmOrgId } from '../../reducers/userInfoReducer';
import { CrmOrgPermission } from '../../permissions/permissions.types';
import { useRunOnceWhenTruthy } from '../common/useRunOnceWhenTruthy';
import EmptyStateWithButton from '../common/empty-state/EmptyStateWithButton';
import EarthImage from '../common/empty-state/svgs/earth.svg?react';
import TerritoriesTable from './TerritoriesTable';
import { DialogLoader } from '../common/dialogs/DialogLoader';
import { SearchInput } from '../common/SearchInput';
import pluralize from 'pluralize';
import GroupsTerritoriesLayout from './GroupsTerritoriesLayout';
import { useRoutingContext } from './RoutingContext';
import { SEARCH_BOX_WIDTH } from '../Automations/helper';
import useQueryParams from '../../navigation-utils/useQueryParams';
import { DeepLinkSearchParams } from '@server/deep-link-search-params';
import { extractShortUUID } from '../../navigation-utils/readableUUID';
import { Territory } from '../../types/Territories';

interface TerritoriesProps {
  onClose?: () => void;
  isDialogLayout?: boolean;
}

const TerritoriesPageContent = ({ onClose, isDialogLayout }: TerritoriesProps) => {
  const territories = useSelector(selectTerritories);
  const { createTerritory, updateTerritory, deleteTerritory } = useTerritoriesActions();
  const [createEditDialogOpen, setCreateEditDialogOpen] = useState(false);
  const crmOrgId = useSelector(selectDefaultCreationCrmOrgId) ?? '';
  const permissionStr: CrmOrgPermission[] = ['create:territories', 'edit:territories'];
  const { evaluatePermissions } = usePermissions();
  const [isAllowedBtnCreate, isAllowedBtnEdit] = evaluatePermissions(
    [{ permission: permissionStr, crmOrgId }]
  );
  const [editableObject, setEditableObject] = useState<Partial<Territory>>();
  const { territoriesSearch, setTerritoriesSearch } = useRoutingContext();
  const territoriesWithSearch = territories?.filter((territory) =>
    territory.name.toLowerCase().includes(territoriesSearch.toLowerCase()),
  );

  const query = useQueryParams();

  useEffect(() => {
    const territoryId = extractShortUUID(query.get(DeepLinkSearchParams.territoryId));
    if (territoryId && territories) {
      const territory = territories?.find((territory) => territory.id.startsWith(territoryId));
      if (territory) {
        setEditableObject(territory);
        setCreateEditDialogOpen(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [territories]);

  const setEditAndOpenDialog = (territory?: Partial<Territory>) => {
    setEditableObject(territory);
    setCreateEditDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setCreateEditDialogOpen(false);
  };

  const handleUpdateObject = async (_territory: Territory, isEdit: boolean) => {
    isEdit ? await updateTerritory(_territory.id, _territory) : await createTerritory(_territory);
    setCreateEditDialogOpen(false);
  };

  const handleDelete = async (toDelete: Territory) => {
    await deleteTerritory(toDelete.id);
  };

  const isEmpty = territories?.length === 0;

  const renderContent = () => {
    if (isEmpty) {
      return (
        <Stack pt="160px">
          <EmptyStateWithButton
            image={<EarthImage />}
            line1="Optimize and create new assignment Territories"
            notAllowedTitle="To create a new territory, please contact your admin."
            permissionQuery={[{ permission: permissionStr[0], crmOrgId }]}
            onClick={() => setEditAndOpenDialog()}
          />
        </Stack>
      );
    }

    return (
      <>
        <DialogLoader visible={!territories} />
        {territories && territoriesWithSearch && (
          <TerritoriesTable
            territories={territoriesWithSearch}
            onEdit={setEditAndOpenDialog}
            onDuplicate={(territory) => {
              const newItem = {
                ...territory,
                name: 'Copy of ' + territory.name,
                id: undefined,
              };
              setEditAndOpenDialog(newItem);
            }}
            onDelete={handleDelete}
            onClearFilters={() => setTerritoriesSearch('')}
          />
        )}
      </>
    );
  };

  const newTerritoryButtonJsx = (
    <RestrictedTooltip
      validation={{ type: 'query', queries: permissionStr.map(permission => ({ permission, crmOrgId })) }}
      notAllowedTitle={`To create a new territory, please contact your admin.`}
    >
      <Button
        onClick={() => setEditAndOpenDialog()}
        startIconName="Plus"
        variant="primary"
        disabled={!isAllowedBtnCreate}
        size="small"
      >
        New territory
      </Button>
    </RestrictedTooltip>
  );

  const renderDialogs = () => {
    if (createEditDialogOpen) {
      return (
        <CreateEditTerritoryDialog
          isOpen={createEditDialogOpen}
          onBackButtonClick={handleCloseDialog}
          initialTerritory={editableObject}
          onSave={handleUpdateObject}
          isAllowedBtn={isAllowedBtnEdit}
          readonly={!isAllowedBtnCreate || !isAllowedBtnEdit}
        />
      );
    }
  };

  const searchJsx = (
    <Box width={SEARCH_BOX_WIDTH}>
      <SearchInput
        withFixedMagnifyingGlassIcon
        TextFieldProps={{
          value: territoriesSearch,
          placeholder: 'Search territories',
          onChange: (e) => {
            setTerritoriesSearch(e.target.value);
          },
        }}
        onClearButtonClick={() => setTerritoriesSearch('')}
        size="small"
      />
    </Box>
  );

  return (
    <GroupsTerritoriesLayout
      title="Territories"
      isDialogLayout={isDialogLayout}
      onClose={onClose}
      newButtonJsx={newTerritoryButtonJsx}
      searchJsx={searchJsx}
      countText={pluralize('territories', territories?.length, true)}
      renderContent={renderContent}
      renderDialogs={renderDialogs}
    />
  );
};

const Territories = (props: TerritoriesProps) => {
  const territories = useSelector(selectTerritories);
  const { getTerritories } = useTerritoriesActions();
  useRunOnceWhenTruthy(async () => {
    await getTerritories();
  }, !!territories);

  return <TerritoriesPageContent {...props} />;
};

export default Territories;
